import { accessToken } from '../utils/Tokens';

import {
  LOGIN,
  LOGOUT,
  DELETE_ME,
  NEW_ACCESS_TOKEN_RECEIVED,
  API_REQUEST_NOT_AUTHORIZED,
  SEND_LOGIN_EMAIL,
  UPDATE_MAINTENANCE_WINDOW,
  UPDATE_DATA_PROCESSING_QUEUE,
  UPDATE_LOAD_STATUS,
} from '../actions/actionTypes';
import { ApiError, applyTokenPermissions, UpstreamToken } from 'upstream-shared/models';
import { DataProcessingQueueEntry } from '../upstreamApi/apiActions';

export interface AuthState {
  accessToken?: string;
  error?: string;
  isLoggingIn: boolean;
  requestedFreeDoc?: boolean;
  authUser?: UpstreamToken;
  nextMaintenance?: Date;
  dataProcessingQueue?: DataProcessingQueueEntry[];
  loadStartedAt?: Date | null;
  loadUpdatedAt?: Date | null;
  loadStatus?: string | null;
}

const getLoggedOutState = () => ({
  isLoggingIn: false,
  error: '',
  requestedFreeDoc: false,
});

const getUserFromAccessToken = () => {
  const token = accessToken.decode();
  const t2 =
    (token?.sub &&
      applyTokenPermissions({
        userId: token.sub,
        email: token.email,
        scope: token.scope,
        firstName: token.firstName,
        lastName: token.lastName,
        role: token.role,
      })) ||
    undefined;
  return t2;
};

const getInitialState = (): AuthState => {
  try {
    return {
      ...getLoggedOutState(),
      accessToken: accessToken.get(),
      authUser: getUserFromAccessToken(),
    };
  } catch (err) {
    // tslint:disable-next-line:no-console
    console.log('Error loading initial auth state.', err);
    return getLoggedOutState();
  }
};

export const authReducer = (state = getInitialState(), action: any) => {
  let error;

  switch (action.type) {
    case SEND_LOGIN_EMAIL.REQUESTED:
    case SEND_LOGIN_EMAIL.SUCCEEDED:
      return {
        ...getLoggedOutState(),
      };

    case LOGIN.REQUESTED:
      return {
        ...state,
        isLoggingIn: true,
      };

    case LOGIN.SUCCEEDED:
      return {
        ...state,
        isLoggingIn: false,
        requestedFreeDoc: false,
      };

    case NEW_ACCESS_TOKEN_RECEIVED:
      return {
        ...getLoggedOutState(),
        accessToken: accessToken.get(),
        authUser: getUserFromAccessToken(),
      };

    case UPDATE_MAINTENANCE_WINDOW:
      return {
        ...state,
        nextMaintenance: action.payload,
      };

    case UPDATE_DATA_PROCESSING_QUEUE:
      return {
        ...state,
        dataProcessingQueue: action.payload,
      };

    case UPDATE_LOAD_STATUS:
      return {
        ...state,
        loadStartedAt: action.payload.loadStartedAt,
        loadUpdatedAt: action.payload.loadUpdatedAt,
        loadStatus: action.payload.loadStatus ?? null,
      };

    case API_REQUEST_NOT_AUTHORIZED:
      error = action.payload.error as ApiError;
      const requestedFreeDoc: boolean = action.payload.requestedFreeDoc;
      return {
        ...getLoggedOutState(),
        accessToken: accessToken.get(),
        authUser: getUserFromAccessToken(),
        error: `${error ? `${error.name}: ${error.message}` : 'no message'}`,
        requestedFreeDoc,
      };

    case LOGIN.FAILED:
      error = action.payload as ApiError;
      return {
        ...getLoggedOutState(),
        accessToken: accessToken.get(),
        authUser: getUserFromAccessToken(),
        error: `${error ? `${error.name}: ${error.message}` : 'no message'}`,
      };

    case LOGOUT.SUCCEEDED:
    case LOGOUT.FAILED:
      return {
        ...getLoggedOutState(),
      };
    case DELETE_ME.REQUESTED:
      return state;
    case DELETE_ME.SUCCEEDED:
      return {
        ...getLoggedOutState(),
      };
    case DELETE_ME.FAILED:
      error = action.payload as ApiError;
      return {
        ...state,
        error: `${error ? `${error.name}: ${error.message}` : 'no message'}`,
      };

    default:
      return state;
  }
};

export const getAuthUser = (state: { auth: AuthState }) => {
  return state.auth.authUser;
};

export const getAuthError = (state: { auth: AuthState }) => {
  return state.auth.error;
};

export const getIsLoggingIn = (state: { auth: AuthState }) => {
  return state.auth.isLoggingIn;
};
